/**
 * Year Editor
 *
 * Annual calendar view for managing schedule periods throughout the year.
 * Allows creating, editing, and deleting date periods.
 */

import { useState, useCallback, useMemo } from 'react';
import type { SchedulePeriod, ScheduleDate } from './types';

/**
 * Month names
 */
const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const FULL_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Days in each month (non-leap year)
 */
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Format a schedule date for display
 */
function formatDate(date: ScheduleDate): string {
  return `${MONTH_NAMES[date.month - 1]} ${String(date.day)}`;
}

/**
 * Get day of year (1-365)
 */
function getDayOfYear(date: ScheduleDate): number {
  let day = date.day;
  for (let m = 0; m < date.month - 1; m++) {
    day += DAYS_IN_MONTH[m] || 0;
  }
  return day;
}

/**
 * Get color for a period (based on index)
 */
const PERIOD_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-amber-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-cyan-500',
  'bg-orange-500',
  'bg-indigo-500',
];

function getPeriodColor(index: number): string {
  return PERIOD_COLORS[index % PERIOD_COLORS.length] || 'bg-gray-500';
}

/**
 * Period row in the list
 */
function PeriodRow({
  period,
  index,
  isSelected,
  onClick,
  onDelete,
  canDelete,
}: {
  period: SchedulePeriod;
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onDelete: () => void;
  canDelete: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 ${
        isSelected
          ? 'border-primary bg-primary/10 dark:bg-primary/20'
          : 'border-border hover:border-primary/50 hover:bg-muted'
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="option"
      tabIndex={0}
      aria-selected={isSelected}
      aria-label={`Period ${index + 1}: ${formatDate(period.startDate)} to ${formatDate(period.endDate)}`}
    >
      <div className={`h-4 w-4 rounded ${getPeriodColor(index)} flex-shrink-0`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground">
          {formatDate(period.startDate)} - {formatDate(period.endDate)}
        </div>
        <div className="text-xs text-muted-foreground">
          {period.daySchedules.length} day type{period.daySchedules.length !== 1 ? 's' : ''} defined
        </div>
      </div>
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="rounded p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring"
          title="Delete period"
          aria-label={`Delete period ${index + 1}`}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * Calendar visualization showing periods
 */
function YearCalendar({ periods }: { periods: SchedulePeriod[] }) {
  // Create a map of day -> period index
  const dayToPeriod = useMemo(() => {
    const map = new Map<number, number>();

    periods.forEach((period, index) => {
      const startDay = getDayOfYear(period.startDate);
      const endDay = getDayOfYear(period.endDate);

      if (startDay <= endDay) {
        for (let d = startDay; d <= endDay; d++) {
          map.set(d, index);
        }
      } else {
        // Wraps around year (e.g., Dec 1 - Jan 31)
        for (let d = startDay; d <= 365; d++) {
          map.set(d, index);
        }
        for (let d = 1; d <= endDay; d++) {
          map.set(d, index);
        }
      }
    });

    return map;
  }, [periods]);

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-medium text-foreground">Annual Overview</h3>
      <div className="grid grid-cols-12 gap-1" role="img" aria-label="Annual calendar showing schedule periods">
        {MONTH_NAMES.map((month, monthIndex) => {
          const daysInMonth = DAYS_IN_MONTH[monthIndex] || 30;
          let dayOfYear = 1;
          for (let m = 0; m < monthIndex; m++) {
            dayOfYear += DAYS_IN_MONTH[m] || 0;
          }

          // Calculate what percentage of the month is covered by each period
          const monthCoverage: { periodIndex: number; percentage: number }[] = [];
          let prevPeriod: number | undefined;
          let currentSpan = 0;

          for (let d = 0; d < daysInMonth; d++) {
            const currentDay = dayOfYear + d;
            const periodIndex = dayToPeriod.get(currentDay);

            if (periodIndex !== prevPeriod) {
              if (prevPeriod !== undefined && currentSpan > 0) {
                monthCoverage.push({
                  periodIndex: prevPeriod,
                  percentage: (currentSpan / daysInMonth) * 100,
                });
              }
              currentSpan = 1;
              prevPeriod = periodIndex;
            } else {
              currentSpan++;
            }
          }

          // Add final span
          if (prevPeriod !== undefined && currentSpan > 0) {
            monthCoverage.push({
              periodIndex: prevPeriod,
              percentage: (currentSpan / daysInMonth) * 100,
            });
          }

          return (
            <div key={month} className="flex flex-col">
              <div className="text-xs text-muted-foreground text-center mb-1">{month}</div>
              <div className="h-6 flex rounded overflow-hidden">
                {monthCoverage.length > 0 ? (
                  monthCoverage.map((coverage, i) => (
                    <div
                      key={i}
                      className={`${getPeriodColor(coverage.periodIndex)}`}
                      style={{ width: `${coverage.percentage}%` }}
                      title={`Period ${coverage.periodIndex + 1}: ${formatDate(periods[coverage.periodIndex]?.startDate ?? { month: 1, day: 1 })} - ${formatDate(periods[coverage.periodIndex]?.endDate ?? { month: 12, day: 31 })}`}
                    />
                  ))
                ) : (
                  <div className="flex-1 bg-muted" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      {periods.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3 border-t border-border pt-3">
          {periods.map((period, index) => (
            <div key={period.id} className="flex items-center gap-1.5 text-xs">
              <div className={`h-3 w-3 rounded ${getPeriodColor(index)}`} aria-hidden="true" />
              <span className="text-muted-foreground">
                Period {index + 1}: {formatDate(period.startDate)} - {formatDate(period.endDate)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Month/Day date picker component
 */
function DateInput({
  label,
  value,
  onChange,
  id,
}: {
  label: string;
  value: ScheduleDate;
  onChange: (date: ScheduleDate) => void;
  id: string;
}) {
  const monthId = `${id}-month`;
  const dayId = `${id}-day`;
  const maxDays = DAYS_IN_MONTH[value.month - 1] || 31;

  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      <div className="flex gap-2">
        <select
          id={monthId}
          value={value.month}
          onChange={(e) => {
            const newMonth = parseInt(e.target.value, 10);
            const newMaxDays = DAYS_IN_MONTH[newMonth - 1] || 31;
            onChange({
              month: newMonth,
              day: Math.min(value.day, newMaxDays),
            });
          }}
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
          aria-label={`${label} month`}
        >
          {FULL_MONTH_NAMES.map((name, i) => (
            <option key={name} value={i + 1}>
              {name}
            </option>
          ))}
        </select>
        <select
          id={dayId}
          value={value.day}
          onChange={(e) => onChange({ ...value, day: parseInt(e.target.value, 10) })}
          className="w-20 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
          aria-label={`${label} day`}
        >
          {Array.from({ length: maxDays }, (_, i) => i + 1).map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

/**
 * Period editor form
 */
function PeriodEditor({
  period,
  onUpdate,
  onCancel,
}: {
  period: SchedulePeriod;
  onUpdate: (updates: Partial<SchedulePeriod>) => void;
  onCancel: () => void;
}) {
  const [startDate, setStartDate] = useState<ScheduleDate>(period.startDate);
  const [endDate, setEndDate] = useState<ScheduleDate>(period.endDate);

  const handleSave = useCallback(() => {
    onUpdate({
      startDate,
      endDate,
    });
  }, [startDate, endDate, onUpdate]);

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-medium text-foreground">Edit Period Dates</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <DateInput
          label="Start Date"
          value={startDate}
          onChange={setStartDate}
          id="period-start"
        />
        <DateInput
          label="End Date"
          value={endDate}
          onChange={setEndDate}
          id="period-end"
        />
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        Period spans from {formatDate(startDate)} to {formatDate(endDate)}
        {getDayOfYear(startDate) > getDayOfYear(endDate) && ' (wraps around year-end)'}
      </p>

      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}

interface YearEditorProps {
  /** Schedule periods */
  periods: SchedulePeriod[];
  /** Currently selected period ID */
  selectedPeriodId: string | null;
  /** Callback when period is selected */
  onSelectPeriod: (id: string | null) => void;
  /** Callback when period is added */
  onAddPeriod: () => void;
  /** Callback when period is deleted */
  onDeletePeriod: (id: string) => void;
  /** Callback when period is updated */
  onUpdatePeriod: (id: string, updates: Partial<SchedulePeriod>) => void;
  /** Whether the editor is read-only */
  readOnly?: boolean;
}

/**
 * Year Editor Component
 */
export function YearEditor({
  periods,
  selectedPeriodId,
  onSelectPeriod,
  onAddPeriod,
  onDeletePeriod,
  onUpdatePeriod,
  readOnly = false,
}: YearEditorProps) {
  const [editingPeriodId, setEditingPeriodId] = useState<string | null>(null);

  const selectedPeriod = periods.find((p) => p.id === selectedPeriodId);
  const editingPeriod = periods.find((p) => p.id === editingPeriodId);

  const handleUpdatePeriod = useCallback(
    (updates: Partial<SchedulePeriod>) => {
      if (editingPeriodId) {
        onUpdatePeriod(editingPeriodId, updates);
        setEditingPeriodId(null);
      }
    },
    [editingPeriodId, onUpdatePeriod]
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Calendar overview */}
      <YearCalendar periods={periods} />

      {/* Period list */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-medium text-foreground">Date Periods</h3>
          {!readOnly && (
            <button
              onClick={onAddPeriod}
              className="flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              Add Period
            </button>
          )}
        </div>

        {periods.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            No periods defined. Add a period to define date ranges.
          </div>
        ) : (
          <div className="space-y-2" role="listbox" aria-label="Schedule periods">
            {periods.map((period, index) => (
              <PeriodRow
                key={period.id}
                period={period}
                index={index}
                isSelected={period.id === selectedPeriodId}
                onClick={() => onSelectPeriod(period.id)}
                onDelete={() => onDeletePeriod(period.id)}
                canDelete={!readOnly && periods.length > 1}
              />
            ))}
          </div>
        )}
      </div>

      {/* Period date editor */}
      {editingPeriod && !readOnly && (
        <PeriodEditor
          period={editingPeriod}
          onUpdate={handleUpdatePeriod}
          onCancel={() => setEditingPeriodId(null)}
        />
      )}

      {/* Period actions */}
      {selectedPeriod && !editingPeriodId && !readOnly && (
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-medium text-foreground">
            Selected: {formatDate(selectedPeriod.startDate)} - {formatDate(selectedPeriod.endDate)}
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setEditingPeriodId(selectedPeriodId)}
              className="flex items-center gap-1 rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
              </svg>
              Edit Dates
            </button>
            {periods.length > 1 && (
              <button
                onClick={() => onDeletePeriod(selectedPeriod.id)}
                className="flex items-center gap-1 rounded-md border border-destructive/50 px-4 py-2 text-sm text-destructive hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
                Delete Period
              </button>
            )}
          </div>
        </div>
      )}

      {/* Help text */}
      <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
        <p className="font-medium text-foreground mb-1">Tip:</p>
        <p>
          Periods define date ranges with different weekly patterns. A simple annual schedule
          typically has one period (Jan 1 - Dec 31). Add multiple periods for seasonal variations
          (e.g., summer vs. winter schedules).
        </p>
      </div>
    </div>
  );
}
